import React, { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '../../lib/utils';

/**
 * Composant d'image optimisé avec:
 * - Lazy loading via Intersection Observer
 * - Skeleton placeholder visible pendant le chargement
 * - Support des thumbnails pour les grilles/listes
 * - Fade-in animation au chargement
 * - Gestion des erreurs
 * - Support des images en position absolue (pour les headers)
 */
export const OptimizedImage = ({
  src,
  alt,
  className,
  containerClassName,
  width,
  height,
  useThumbnail = false,
  priority = false,
  placeholder = 'skeleton',
  onLoad,
  onError,
  // Si l'image doit être positionnée en absolu (ex: header images)
  absolute = false,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  // Détecter si className contient "absolute" pour le mode absolu auto
  const isAbsolute = absolute || (className && className.includes('absolute'));

  // Construire l'URL avec le paramètre thumbnail si nécessaire
  const imageSrc = React.useMemo(() => {
    if (!src) return '';
    if (useThumbnail && src.includes('/api/files/')) {
      const separator = src.includes('?') ? '&' : '?';
      return `${src}${separator}thumb=true`;
    }
    return src;
  }, [src, useThumbnail]);

  // Intersection Observer pour lazy loading
  useEffect(() => {
    if (priority || isAbsolute) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '200px',
        threshold: 0.01
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [priority, isAbsolute]);

  // Précharger les images prioritaires
  useEffect(() => {
    if ((priority || isAbsolute) && imageSrc) {
      const img = new Image();
      img.src = imageSrc;
    }
  }, [priority, isAbsolute, imageSrc]);

  const handleLoad = useCallback((e) => {
    setIsLoaded(true);
    setHasError(false);
    onLoad?.(e);
  }, [onLoad]);

  const handleError = useCallback((e) => {
    setHasError(true);
    setIsLoaded(true);
    onError?.(e);
  }, [onError]);

  // Pour les images absolues (headers), retourner directement l'image sans wrapper
  if (isAbsolute) {
    if (hasError) {
      return (
        <div 
          className={cn(
            "bg-gray-400 flex items-center justify-center text-gray-600",
            className
          )}
          style={{ width, height }}
        >
          <svg 
            className="w-12 h-12" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
            />
          </svg>
        </div>
      );
    }

    return (
      <>
        {/* Skeleton pour image absolue */}
        {!isLoaded && (
          <div 
            className={cn(
              "bg-gray-300",
              className
            )}
            style={{
              background: 'linear-gradient(90deg, #d1d5db 25%, #e5e7eb 50%, #d1d5db 75%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s infinite linear'
            }}
          />
        )}
        <img
          ref={imgRef}
          src={imageSrc}
          alt={alt}
          width={width}
          height={height}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          className={cn(
            "transition-opacity duration-500 ease-out",
            isLoaded ? "opacity-100" : "opacity-0",
            className
          )}
          {...props}
        />
        <style>
          {`
            @keyframes shimmer {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `}
        </style>
      </>
    );
  }

  // Image d'erreur pour mode normal
  if (hasError) {
    return (
      <div 
        className={cn(
          "bg-gray-100 flex items-center justify-center text-gray-400",
          className
        )}
        style={{ width, height }}
      >
        <svg 
          className="w-8 h-8" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={1.5}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
          />
        </svg>
      </div>
    );
  }

  // Mode normal avec conteneur
  return (
    <div ref={containerRef} className={cn("relative overflow-hidden", containerClassName)} style={{ width, height }}>
      {/* Skeleton loader */}
      {!isLoaded && (
        <div 
          className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"
          style={{
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite linear'
          }}
        />
      )}
      
      {/* Image */}
      {isInView && (
        <img
          ref={imgRef}
          src={imageSrc}
          alt={alt}
          width={width}
          height={height}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          className={cn(
            "transition-opacity duration-500 ease-out",
            isLoaded ? "opacity-100" : "opacity-0",
            className
          )}
          {...props}
        />
      )}
      
      <style>
        {`
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}
      </style>
    </div>
  );
};

export default OptimizedImage;
